/**
 * ──────────────────────────────────────────────────────────────────────────────
 * TEMPORARY PREVIEW PASSWORD GATE — REMOVE BEFORE PUBLIC LAUNCH
 *
 * Backend-controlled. Reads `under_construction` from `/api/settings/site`
 * on mount. Admin can toggle this via `/admin`. While the toggle is ON,
 * visitors must enter the access code to continue. When OFF, this component
 * is a no-op pass-through.
 *
 * To remove entirely:
 *   1. Delete this file (`PreviewGate.jsx`)
 *   2. Remove the import + `<PreviewGate>` wrapper in `/app/frontend/src/App.js`
 *   3. Remove the `/admin` route + AdminPage.jsx if no longer needed
 * ──────────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Lock, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ETERNITY_LOGO =
  "https://customer-assets.emergentagent.com/job_before-the-loss-1/artifacts/i9n653i1_IMG_9898.png";

const ACCESS_CODE = "Eternitymp01";
const STORAGE_KEY = "btr-preview-unlocked";

export default function PreviewGate({ children }) {
  const location = useLocation();
  const onAdminRoute = location.pathname.startsWith("/admin");

  // null = still fetching, true = under-construction ON, false = OFF
  const [gateRequired, setGateRequired] = useState(null);
  const [unlocked, setUnlocked] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem(STORAGE_KEY) === "1";
  });
  const [leaving, setLeaving] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  // Fetch backend setting on mount
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/settings/site`, { timeout: 6000 })
      .then((res) => {
        if (cancelled) return;
        setGateRequired(Boolean(res.data?.under_construction));
      })
      .catch(() => {
        // Fail-safe: if backend unreachable, default to showing the gate
        if (!cancelled) setGateRequired(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const showGate = gateRequired === true && !unlocked && !onAdminRoute;

  useEffect(() => {
    if (!showGate) {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => inputRef.current?.focus(), 600);
    return () => {
      document.body.style.overflow = "";
      clearTimeout(t);
    };
  }, [showGate]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    setTimeout(() => {
      if (value.trim() === ACCESS_CODE) {
        window.sessionStorage.setItem(STORAGE_KEY, "1");
        setLeaving(true);
        setTimeout(() => {
          setUnlocked(true);
          document.body.style.overflow = "";
        }, 700);
      } else {
        setError("Incorrect access code. Please try again.");
        setSubmitting(false);
        setValue("");
        inputRef.current?.focus();
      }
    }, 350);
  };

  if (gateRequired === null && !unlocked && !onAdminRoute) {
    // Brief boot state while we ask the backend
    return (
      <div className="fixed inset-0 z-[300] bg-[#050505] flex items-center justify-center text-zinc-600 text-xs tracking-[0.3em] uppercase font-body">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading
      </div>
    );
  }

  if (!showGate) return children;

  return (
    <div
      className={`btr-gate ${leaving ? "is-leaving" : ""}`}
      data-testid="preview-gate"
      role="dialog"
      aria-modal="true"
      aria-label="Private preview access"
    >
        {/* Ambient lighting */}
        <div className="btr-gate-bg" />
        <div className="btr-gate-grid" />
        <div className="btr-gate-blueprint" />
        <div className="btr-gate-beam" style={{ top: "-15%", right: "-15%" }} />
        <div className="btr-gate-beam alt" style={{ bottom: "-20%", left: "-10%" }} />

        {/* Wireframe construction marks */}
        <svg
          className="btr-gate-wire"
          viewBox="0 0 1200 800"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden
        >
          <g stroke="#a2020b" strokeWidth="0.7" fill="none" opacity="0.35">
            <path d="M 0 600 L 1200 600" strokeDasharray="3 6" />
            <path d="M 0 200 L 1200 200" strokeDasharray="3 6" />
            <path d="M 200 0 L 200 800" strokeDasharray="3 6" />
            <path d="M 1000 0 L 1000 800" strokeDasharray="3 6" />
            <circle cx="200" cy="200" r="6" fill="#a2020b" />
            <circle cx="1000" cy="200" r="6" fill="#a2020b" />
            <circle cx="200" cy="600" r="6" fill="#a2020b" />
            <circle cx="1000" cy="600" r="6" fill="#a2020b" />
            <path d="M 200 200 L 1000 200 L 1000 600 L 200 600 Z" strokeWidth="1.1" />
            <path d="M 200 200 L 1000 600" opacity="0.5" />
            <path d="M 1000 200 L 200 600" opacity="0.5" />
          </g>
        </svg>

        {/* Floating red dots */}
        {Array.from({ length: 14 }).map((_, i) => (
          <span
            key={i}
            className="btr-gate-dot"
            style={{
              left: `${(i * 73) % 100}%`,
              top: `${(i * 41) % 100}%`,
              animationDelay: `${(i * 0.7) % 6}s`,
              animationDuration: `${8 + (i % 5)}s`,
            }}
          />
        ))}

        <div className="btr-gate-scan" />

        <div className="btr-gate-stage">
          {/* Eternity logo block */}
          <div className="btr-gate-logo-wrap">
            <span className="btr-gate-sweep" aria-hidden />
            <img
              src={ETERNITY_LOGO}
              alt="Eternity"
              className="btr-gate-logo"
              decoding="async"
              fetchpriority="high"
            />
          </div>

          {/* Status pill */}
          <div className="btr-gate-pill">
            <span className="dot" />
            UNDER CONSTRUCTION
          </div>

          <h1 className="btr-gate-title">
            This website is currently <span>under construction</span>.
          </h1>
          <p className="btr-gate-sub">Powered by Eternity MP.</p>
          <p className="btr-gate-fine">Private preview access only.</p>

          <form onSubmit={handleSubmit} className="btr-gate-form" data-testid="preview-form">
            <label className="btr-gate-label">Enter Access Code</label>
            <div className="btr-gate-input-row">
              <Lock className="btr-gate-input-icon" />
              <input
                ref={inputRef}
                type="password"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
                placeholder="••••••••••"
                autoComplete="off"
                inputMode="text"
                className="btr-gate-input"
                data-testid="preview-input"
                aria-label="Access code"
              />
            </div>
            <button
              type="submit"
              className="btr-gate-btn"
              disabled={submitting}
              data-testid="preview-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Verifying
                </>
              ) : (
                <>
                  Unlock Preview <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            {error && (
              <div className="btr-gate-error" data-testid="preview-error" role="alert">
                {error}
              </div>
            )}
          </form>

          {/* Progress strip */}
          <div className="btr-gate-progress">
            <span />
          </div>

          <div className="btr-gate-foot">
            <span className="btr-gate-foot-line" />
            <span className="btr-gate-foot-txt">ETERNITYMP.COM · PRIVATE STAGING</span>
            <span className="btr-gate-foot-line" />
          </div>
        </div>
      </div>
  );
}
